import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Facebook,
  Instagram,
  LinkedIn,
  YouTube,
  Email,
  Phone,
  LocationOn,
  ArrowUpward,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

import logo from '../assets/images/almondlogo.svg';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  const iconHoverVariants = {
    hover: {
      scale: 1.1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 15,
      },
    },
  };

  // Social media links configuration
  const socialLinks = [
    {
      icon: <Facebook />,
      url: 'https://www.facebook.com/people/Almond-Stay/61588973794374/',
      label: 'Facebook',
    },
    {
      icon: <Instagram />,
      url: 'https://www.instagram.com/almond_stay_official',
      label: 'Instagram',
    },
    {
      icon: <LinkedIn />,
      url: 'https://in.linkedin.com/company/almond-stay-official',
      label: 'LinkedIn',
    },
    { icon: <YouTube />, url: 'https://www.youtube.com/@Almond_Stay_Official', label: 'YouTube' },
  ];

  // Support links
  const supportLinks = [{ text: 'Help & Support', url: '/policy/host-help' }];

  // Policy links
  const policyLinks = [
    { text: 'Privacy Policy', url: '/policy/privacy-policy' },
    { text: 'Terms & Conditions', url: '/policy/terms-and-conditions' },
    { text: 'Host Policy', url: '/policy/host-policy' },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={containerVariants}>
      <Box
        component="footer"
        sx={{
          bgcolor: '#0f0f1a',
          color: '#fff',
          pt: { xs: 3.5, sm: 4 },
          pb: 2,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          {/* Main Footer Content */}
          <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
            {/* Brand Section */}
            <Grid item xs={12} md={5}>
              <motion.div variants={itemVariants}>
                <Box sx={{ mb: 1.5 }}>
                  <img
                    src={logo}
                    alt="Almond Stay"
                    style={{
                      width: '170px',
                      height: 'auto',
                      display: 'block',
                      marginBottom: '10px',
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#aaa', maxWidth: 400, mb: 2, fontSize: '0.85rem' }}>
                  Your trusted platform for seamless stays and unforgettable experiences. Book
                  unique homes and experience travel like never before.
                </Typography>

                {/* Contact Info */}
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  <motion.div variants={itemVariants}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn sx={{ color: '#e0b354', fontSize: 18 }} />
                      <Typography variant="body2" sx={{ color: '#aaa', fontSize: '0.85rem' }}>
                        Office no 114, Shivalik, Bhumipark Society, Dabholi, Surat, Gujarat 395004
                      </Typography>
                    </Box>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email sx={{ color: '#e0b354', fontSize: 18 }} />
                      <Link
                        href="mailto:support@almondstay.com"
                        color="inherit"
                        underline="hover"
                        sx={{ color: '#aaa', fontSize: '0.85rem', '&:hover': { color: '#e0b354' } }}
                      >
                        contact@almondstay.com
                      </Link>
                    </Box>
                  </motion.div>
                  <motion.div variants={itemVariants}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone sx={{ color: '#e0b354', fontSize: 18 }} />
                      <Link
                        href="tel:+1234567890"
                        color="inherit"
                        underline="hover"
                        sx={{ color: '#aaa', fontSize: '0.85rem', '&:hover': { color: '#e0b354' } }}
                      >
                        +91 9979655151
                      </Link>
                    </Box>
                  </motion.div>
                </Stack>

                {/* Social Media Links */}
                <Box sx={{ mt: 2.5 }}>
                  <Typography variant="body2" sx={{ color: '#aaa', mb: 1, fontSize: '0.85rem' }}>
                    Follow Us
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {socialLinks.map((social, index) => (
                      <Tooltip title={social.label} key={index}>
                        <motion.div variants={iconHoverVariants} whileHover="hover">
                          <IconButton
                            component="a"
                            href={social.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: '#ccc',
                              bgcolor: 'rgba(255,255,255,0.05)',
                              '&:hover': {
                                bgcolor: '#e0b354',
                                color: '#0f0f1a',
                                transform: 'translateY(-2px)',
                                transition: 'all 0.3s ease',
                              },
                              transition: 'all 0.3s ease',
                            }}
                            size="small"
                          >
                            {social.icon}
                          </IconButton>
                        </motion.div>
                      </Tooltip>
                    ))}
                  </Stack>
                </Box>
              </motion.div>
            </Grid>

            {/* Support Section */}
            <Grid item xs={6} sm={4} md={3}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    color: '#e0b354',
                    letterSpacing: 0.5,
                  }}
                >
                  Support
                </Typography>
                <Stack spacing={1}>
                  {supportLinks.map((link, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: 5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <Link
                        href={link.url}
                        color="inherit"
                        underline="hover"
                        sx={{
                          color: '#ccc',
                          '&:hover': { color: '#e0b354' },
                          transition: 'color 0.2s ease',
                          fontSize: '0.85rem',
                        }}
                      >
                        {link.text}
                      </Link>
                    </motion.div>
                  ))}
                </Stack>
              </motion.div>
            </Grid>

            {/* Policies Section */}
            <Grid item xs={6} sm={4} md={3}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    color: '#e0b354',
                    letterSpacing: 0.5,
                  }}
                >
                  Policies
                </Typography>
                <Stack spacing={1}>
                  {policyLinks.map((link, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: 5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <Link
                        href={link.url}
                        color="inherit"
                        underline="hover"
                        sx={{
                          color: '#ccc',
                          '&:hover': { color: '#e0b354' },
                          transition: 'color 0.2s ease',
                          fontSize: '0.85rem',
                        }}
                      >
                        {link.text}
                      </Link>
                    </motion.div>
                  ))}
                </Stack>
              </motion.div>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 2, mb: 1.5 }} />

          {/* Bottom Bar */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1.5,
              pt: 0.5,
            }}
          >
            <motion.div variants={itemVariants}>
              <Typography variant="body2" sx={{ color: '#666', fontSize: '0.78rem' }}>
                © {currentYear} Almond Stay. All rights reserved.
              </Typography>
            </motion.div>
          </Box>
        </Container>
      </Box>
    </motion.div>
  );
};

export default Footer;
